import { FC, useMemo } from 'react'
import { useGetPartnerAllList } from 'framework/api/methods'

import { SimpleSelect } from 'components/atoms/Select'

export interface PartnerFilterOption {
  id: string
  value: string
  label: string
  code: string
}

export const ALL_PARTNERS_OPTION: PartnerFilterOption = {
  id: '',
  value: '',
  label: 'All partners',
  code: '',
}

export interface PartnerFilterDropdownProps {
  value: PartnerFilterOption
  onChange: (option: PartnerFilterOption) => void
}

export const formatPartnerOptionLabel = (option: PartnerFilterOption) => (
  <div className='flex items-center gap-2'>
    <span className='font-semibold text-N-800 truncate'>{option.label}</span>
    {option.code && (
      <span className='text-[11px] font-semibold text-N-500 bg-N-100 rounded px-2 py-[1px]'>
        {option.code}
      </span>
    )}
  </div>
)

// react-select's default filterOption only matches against `label`; the design's "Search partners…"
// behaviour also needs to match the partner code, so a custom filterOption is required.
export const filterPartnerOption = (
  candidate: { label: string; value: string; data: PartnerFilterOption },
  input: string,
) => {
  if (!input) return true
  const term = input.toLowerCase()
  return (
    candidate.data.label.toLowerCase().includes(term) ||
    candidate.data.code.toLowerCase().includes(term)
  )
}

/** Active partners as select options (no "All partners" entry). Shared by the filter and the New Email Group drawer. */
export const usePartnerOptions = () => {
  const { isLoading, data } = useGetPartnerAllList()

  const options = useMemo<PartnerFilterOption[]>(() => {
    return (data?.partners ?? [])
      .filter((partner: any) => partner.isActive !== false)
      .map((partner: any) => ({
        id: partner.id,
        value: partner.id,
        label: partner.name,
        code: partner.code ?? '',
      }))
  }, [data])

  return { isLoading, options }
}

export const PartnerFilterDropdown: FC<PartnerFilterDropdownProps> = ({
  value,
  onChange,
}: PartnerFilterDropdownProps) => {
  const { isLoading, options: partnerOptions } = usePartnerOptions()

  const options = useMemo<PartnerFilterOption[]>(
    () => [ALL_PARTNERS_OPTION, ...partnerOptions],
    [partnerOptions],
  )

  return (
    <div className='w-[340px]'>
      <div className='text-[11.5px] font-bold text-N-500 tracking-wide mb-[6px]'>
        PARTNER
      </div>
      <SimpleSelect
        isLoading={isLoading}
        placeholder='Select partner…'
        value={value}
        onChange={onChange}
        options={options}
        isClearable={false}
        // SimpleSelect only forwards the `restProps` object to react-select (BDL20-4262).
        restProps={{
          formatOptionLabel: formatPartnerOptionLabel,
          filterOption: filterPartnerOption,
        }}
      />
    </div>
  )
}

export default PartnerFilterDropdown
